import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import PersistLogin from '../components/Auth/PersistLogin'
import RequireAuth from '../components/Auth/RequireAuth'
import Unauthorized from '../components/Auth/Unauthorized'
import Loading from '../components/Loading/Loading'
import LogOut from '../components/LogOut/LogOut'
import Missing from '../components/Missing/Missing'
import Layout from '../layouts/Layout'
import { Permission } from '../types/User'
import { MODE_SELECTOR_PATH, PLATFORM_ROOT } from '../utils/platformMode'

// Route-level code splitting: each page is emitted as its own chunk so the
// initial bundle stays small (and under the PWA precache size limit).
const EmployeeJobType = lazy(() => import('../pages/Configuration/EmployeeJobType'))
const EmployeeJobTypeCreate = lazy(() => import('../pages/Configuration/EmployeeJobTypeCreate'))
const EmployeeJobTypeDetails = lazy(() => import('../pages/Configuration/EmployeeJobTypeDetails'))
const EmployeeJobTypeEdit = lazy(() => import('../pages/Configuration/EmployeeJobTypeEdit'))
const ClientFarmJobTypes = lazy(() => import('../pages/Configuration/ClientFarmJobTypes'))
const ClientFarmProductTypes = lazy(() => import('../pages/Configuration/ClientFarmProductTypes'))
const ClientFarmExpenseTypes = lazy(() => import('../pages/Configuration/ClientFarmExpenseTypes'))
const CreateDailyWorkReport = lazy(() => import('../pages/DailyWorkReport/CreateDailyWorkReport'))
const DailyWorkReport = lazy(() => import('../pages/DailyWorkReport/DailyWorkReport'))
const EditDailyWorkReport = lazy(() => import('../pages/DailyWorkReport/EditDailyWorkReport'))
const Employee = lazy(() => import('../pages/Employee/Employee'))
const EmployeeCreate = lazy(() => import('../pages/Employee/EmployeeCreate'))
const EmployeeDetails = lazy(() => import('../pages/Employee/EmployeeDetails'))
const EmployeeEdit = lazy(() => import('../pages/Employee/EmployeeEdit'))
const EmployeeHistory = lazy(() => import('../pages/Employee/EmployeeHistory'))
const EmployeeLoan = lazy(() => import('../pages/Employee/EmployeeLoan'))
const EmployeeLoanCreate = lazy(() => import('../pages/Employee/EmployeeLoanCreate'))
const EmployeeLoanDetails = lazy(() => import('../pages/Employee/EmployeeLoanDetails'))
const EmployeeLoanPaymentCreate = lazy(() => import('../pages/Employee/EmployeeLoanPaymentCreate'))
const Farm = lazy(() => import('../pages/Farm/Farm'))
const FarmAcitivityDetails = lazy(() => import('../pages/Farm/FarmAcitivityDetails'))
const FarmCreate = lazy(() => import('../pages/Farm/FarmCreate'))
const FarmDetails = lazy(() => import('../pages/Farm/FarmDetails'))
const FarmEdit = lazy(() => import('../pages/Farm/FarmEdit'))
const FarmExpenseCreate = lazy(() => import('../pages/Farm/FarmExpenseCreate'))
const FarmExpenseEdit = lazy(() => import('../pages/Farm/FarmExpenseEdit'))
const FarmProfitCreate = lazy(() => import('../pages/Farm/FarmProfitCreate'))
const FarmProfitEdit = lazy(() => import('../pages/Farm/FarmProfitEdit'))
const FarmProjectCreate = lazy(() => import('../pages/Farm/FarmProjectCreate'))
const FarmProjectDetails = lazy(() => import('../pages/Farm/FarmProjectDetails'))
const FarmProjectEdit = lazy(() => import('../pages/Farm/FarmProjectEdit'))
const Home = lazy(() => import('../pages/Home'))
const LoginPage = lazy(() => import('../pages/Login'))
const CalculatePayroll = lazy(() => import('../pages/Payroll/CalculatePayroll'))
const Payroll = lazy(() => import('../pages/Payroll/Payroll'))
const PayrollDetails = lazy(() => import('../pages/Payroll/PayrollDetails'))
const CreateProductInventory = lazy(() => import('../pages/ProductInventory/CreateProductInventory'))
const EditProductInventory = lazy(() => import('../pages/ProductInventory/EditProductInventory'))
const ProductInventory = lazy(() => import('../pages/ProductInventory/ProductInventory'))
const ProductInventoryHistory = lazy(() => import('../pages/ProductInventory/ProductInventoryHistory'))
const User = lazy(() => import('../pages/User/User'))
const UserCreate = lazy(() => import('../pages/User/UserCreate'))
const UserDetails = lazy(() => import('../pages/User/UserDetails'))
const UserEdit = lazy(() => import('../pages/User/UserEdit'))
const Project = lazy(() => import('../pages/Farm/Project'))
const Reports = lazy(() => import('../pages/Reports/Reports'))
const ModeSelector = lazy(() => import('../pages/Platform/ModeSelector'))
const PlatformHome = lazy(() => import('../pages/Platform/PlatformHome'))
const GlobalCatalog = lazy(() => import('../pages/Platform/GlobalCatalog'))
const PlatformClients = lazy(() => import('../pages/Platform/PlatformClients'))
const PlatformClientDetail = lazy(() => import('../pages/Platform/PlatformClientDetail'))
const PlatformAudit = lazy(() => import('../pages/Platform/PlatformAudit'))
const PlatformProjectTemplates = lazy(() => import('../pages/Platform/ProjectTemplates'))
const PlatformProjectTemplateDetail = lazy(() => import('../pages/Platform/ProjectTemplateDetail'))
const ProjectTemplates = lazy(() => import('../pages/Farm/ProjectTemplates'))
const ProjectTemplateDetail = lazy(() => import('../pages/Farm/ProjectTemplateDetail'))
const PlantedHectaresReport = lazy(() => import('../pages/Reports/PlantedHectaresReport'))
const UnplantedHectaresReport = lazy(() => import('../pages/Reports/UnplantedHectaresReport'))

const Router: React.FC = () => {
  return (
    <Suspense fallback={<Loading />}>
    <Routes>
      <Route path='/' element={<Layout />}>
        {/* public routes */}
        <Route path='login' element={<LoginPage />} />
        <Route path='unauthorized' element={<Unauthorized />} />

        {/* we want to protect these routes */}
        <Route element={<PersistLogin />}>
          {/* No permission required */}
          <Route element={<RequireAuth allowedPermissions={[]} />}>
            <Route path='/' element={<Home />} />
            <Route path='reporte-trabajo' element={<DailyWorkReport />} />
            <Route path='inventario-productos' element={<ProductInventory />} />
            <Route path='inventario-productos/historial' element={<ProductInventoryHistory />} />
            <Route path='empleados' element={<Employee />} />
            <Route path='empleados/detalles/:id' element={<EmployeeDetails />} />
            <Route path='fincas' element={<Farm />} />
            <Route path='fincas/detalles/:id' element={<FarmDetails />} />
          </Route>

          <Route element={<RequireAuth allowedPermissions={[Permission.ViewSettings, Permission.AdminSettings]}/>}>
            <Route path='trabajos-fincas' element={<ClientFarmJobTypes />} />
            <Route path='productos-fincas' element={<ClientFarmProductTypes />} />
            <Route path='tipos-gastos-fincas' element={<ClientFarmExpenseTypes />} />
            <Route path='trabajos-empleados' element={<EmployeeJobType />} />
            <Route path='trabajos-empleados/detalles/:id' element={<EmployeeJobTypeDetails />}/>
            <Route path='usuarios' element={<User />} />
          </Route>

          <Route element={<RequireAuth allowedPermissions={[Permission.IsAdmin]}/>}>
            <Route path='usuarios/editar/:id' element={<UserEdit />} />
            <Route path='usuarios/agregar' element={<UserCreate />} />
          </Route>

          {/* Create/Edit Daily Work Report */}
          <Route element={<RequireAuth allowedPermissions={[Permission.CreateAndEditDailyWorkReport]} />}>
            <Route path='reporte-trabajo/agregar' element={<CreateDailyWorkReport />} />
            <Route path='reporte-trabajo/editar/:reportId' element={<EditDailyWorkReport />} />
          </Route>

          {/* Payroll: View and Admin */}
          <Route element={<RequireAuth allowedPermissions={[Permission.ViewCalculatedPayroll, Permission.AdminPayroll]} />}>
            <Route path='planilla/calcular-pagar' element={<CalculatePayroll />} />
          </Route>
          <Route element={<RequireAuth allowedPermissions={[Permission.ViewPayrollHistory, Permission.AdminPayroll]} />}>
            <Route path='planilla/detalles/:payrollMasterId' element={<PayrollDetails />} />
            <Route path='planilla' element={<Payroll />} />
          </Route>

          {/* Admin Products */}
          <Route element={<RequireAuth allowedPermissions={[Permission.AdminProducts]} />}>
            <Route path='inventario-productos/agregar' element={<CreateProductInventory />} />
            <Route path='inventario-productos/editar/:productId' element={<EditProductInventory />} />
          </Route>

          {/* Admin Employees */}
          <Route element={<RequireAuth allowedPermissions={[Permission.AdminEmployees]} />}>
            <Route path='empleados/editar/:id' element={<EmployeeEdit />} />
            <Route path='empleados/agregar' element={<EmployeeCreate />} />
            <Route path='empleados/agregar' element={<EmployeeCreate />} />
            <Route path='empleados/prestamos/:id' element={<EmployeeLoan />} />
            <Route path='empleados/prestamos/agregar/:employeeId' element={<EmployeeLoanCreate />} />
            <Route path='empleados/prestamos/detalles/:id' element={<EmployeeLoanDetails />} />
            <Route path='empleados/prestamos/pagos/agregar/:employeeLoanId' element={<EmployeeLoanPaymentCreate />} />
          </Route>
         
          <Route
            element={
              <RequireAuth
                allowedPermissions={[Permission.AdminEmployees, Permission.AdminPayroll]}
              />
            }
          >
            <Route path='empleados/historial/:id' element={<EmployeeHistory />} />
          </Route>

          {/* Admin Settings */}
          <Route element={<RequireAuth allowedPermissions={[Permission.AdminSettings]} />}>
            <Route path='trabajos-empleados/editar/:id' element={<EmployeeJobTypeEdit />} />
            <Route path='trabajos-empleados/agregar' element={<EmployeeJobTypeCreate />} />
          </Route>

          {/* Admin Farms */}
          <Route element={<RequireAuth allowedPermissions={[Permission.AdminFarms]} />}>
            <Route path='reportes' element={<Reports />} />
            <Route path='reportes/hectareas-sembradas' element={<PlantedHectaresReport />} />
            <Route path='reportes/hectareas-sin-sembrar' element={<UnplantedHectaresReport />} />
            <Route path='proyectos' element={<Project />} />
            <Route path='plantillas-proyecto' element={<ProjectTemplates />} />
            <Route path='plantillas-proyecto/:id' element={<ProjectTemplateDetail />} />
            <Route path='fincas/editar/:id' element={<FarmEdit />} />
            <Route path='fincas/agregar' element={<FarmCreate />} />
            <Route path='fincas/proyecto-crear/:farmId' element={<FarmProjectCreate />} />
            <Route path='fincas/proyecto-editar/:farmProjectId' element={<FarmProjectEdit />} />
            <Route path='fincas/proyecto-detalles/:id' element={<FarmProjectDetails />} />
            <Route path='fincas/ingreso-agregar/:farmId' element={<FarmProfitCreate />} />
            <Route path='fincas/ingreso-agregar/proyecto/:farmProjectId' element={<FarmProfitCreate />} />
            <Route path='fincas/ingreso-editar/:id' element={<FarmProfitEdit />} />
            <Route path='fincas/gasto-agregar/:farmId' element={<FarmExpenseCreate />} />
            <Route path='fincas/gasto-editar/:id' element={<FarmExpenseEdit />} />
            <Route path='fincas/gasto-agregar/proyecto/:farmProjectId' element={<FarmExpenseCreate />} />
            <Route path='fincas/actividades/:farmId' element={<FarmAcitivityDetails />} />
          </Route>

          {/* Both identities: client user and platform administrator */}
          <Route element={<RequireAuth scope='any' />}>
            <Route path={MODE_SELECTOR_PATH} element={<ModeSelector />} />
          </Route>

          {/* Platform area: global catalogs and client management (PlatformAdmin only) */}
          <Route element={<RequireAuth scope='platform' allowedPermissions={[Permission.PlatformAdmin]} />}>
            <Route path={PLATFORM_ROOT} element={<PlatformHome />} />
            <Route path={`${PLATFORM_ROOT}/catalogos/:slug`} element={<GlobalCatalog />} />
            <Route path={`${PLATFORM_ROOT}/clientes`} element={<PlatformClients />} />
            <Route path={`${PLATFORM_ROOT}/clientes/:clientId`} element={<PlatformClientDetail />} />
            <Route path={`${PLATFORM_ROOT}/bitacora`} element={<PlatformAudit />} />
            <Route path={`${PLATFORM_ROOT}/plantillas`} element={<PlatformProjectTemplates />} />
            <Route path={`${PLATFORM_ROOT}/plantillas/:id`} element={<PlatformProjectTemplateDetail />} />
          </Route>

          {/* Old links: the catalog CRUD moved to the platform, the client only picks its values. */}
          {['trabajos-fincas', 'productos-fincas', 'tipos-gastos-fincas'].map((path) => (
            <Route key={path} path={`${path}/*`} element={<Navigate to={`/${path}`} replace />} />
          ))}
          <Route path='catalogos-cliente' element={<Navigate to='/trabajos-fincas' replace />} />
          <Route path='categorias-inventario/*' element={<Navigate to='/' replace />} />

          {/* logout */}
          <Route path='/logout' element={<LogOut />} />
          <Route path='usuarios/detalles/:id' element={<UserDetails />}/>
        </Route>

        {/* catch all */}
        <Route path='*' element={<Missing />} />
      </Route>
    </Routes>
    </Suspense>
  )
}

export default Router